/**
 * Area — Login (PR-ONB-RESUME-1B, locks #2/#5).
 *
 * Email+password sign-in (Firebase Web SDK, client-side). After login the
 * layout guard re-evaluates GET /area/me; the one case handled HERE is
 * ACCOUNT_NOT_FOUND (Firebase login ok but the bind never completed —
 * signup-ok-bind-failed recovery): the page surfaces a Turnstile-gated
 * "Collega l'account" CTA that calls the IDEMPOTENT bind. Password reset is
 * user-initiated only (standard Firebase email).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { Banner } from '@ab-platform/ui/banner';
import { FieldError } from '@ab-platform/ui/field-error';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PillButton } from '@ab-platform/ui/pill-button';
import { env, resumableAreaEnabled } from '@/lib/env';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { getAreaMe, registerAreaBind } from '@/lib/areaApi';
import {
  bindFailureMessage,
  mapFirebaseLoginError,
} from '@/lib/accountBindFlow';
import { Navigate } from 'react-router-dom';

type Mode = 'login' | 'bind';

export function AreaLoginPage(): JSX.Element {
  const session = useAreaSession();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  if (!resumableAreaEnabled) {
    return <Navigate to="/registrazione-intermediario" replace />;
  }

  const afterAuth = async (): Promise<void> => {
    const token = await session.getToken();
    if (!token) {
      setError('Sessione non valida. Riprova.');
      return;
    }
    const me = await getAreaMe(token);
    if (me.ok) {
      if (me.data.request) {
        session.markBound({
          requestPublicId: me.data.request.publicId,
          draftRevision: me.data.request.draftRevision,
        });
      }
      navigate('/area/stato');
      return;
    }
    if (me.kind === 'ACCOUNT_NOT_FOUND') {
      // Signup-ok-bind-failed recovery: offer the idempotent bind.
      setMode('bind');
      return;
    }
    if (me.kind === 'RESUMABLE_DISABLED') {
      setError('L\'area onboarding non è al momento disponibile. Riprova più tardi.');
      return;
    }
    setError('Accesso all\'area non riuscito. Riprova.');
  };

  const handleLogin = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const fb = await import('@/lib/firebaseClient');
      try {
        await fb.signInWithEmailAndPassword(fb.getFirebaseAuth(), email, password);
      } catch (err) {
        setError(mapFirebaseLoginError((err as { code?: string }).code ?? ''));
        return;
      }
      await afterAuth();
    } finally {
      setBusy(false);
    }
  };

  const handleBind = async (): Promise<void> => {
    setError(null);
    if (!turnstileToken) {
      setError('Completa la verifica anti-abuso per continuare.');
      return;
    }
    setBusy(true);
    try {
      const token = await session.getToken();
      if (!token) {
        setError('Sessione non valida. Riprova.');
        return;
      }
      const result = await registerAreaBind(token, { turnstileToken });
      if (!result.ok) {
        setError(bindFailureMessage(result));
        if (result.kind === 'TURNSTILE_INVALID') {
          setTurnstileToken('');
          setTurnstileKey((k) => k + 1);
        }
        return;
      }
      session.markBound({
        requestPublicId: result.data.requestPublicId,
        draftRevision: result.data.draftRevision,
      });
      navigate('/area/stato');
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordReset = async (): Promise<void> => {
    setError(null);
    if (!email) {
      setError('Inserisci la tua email per ricevere il link di reset.');
      return;
    }
    try {
      const fb = await import('@/lib/firebaseClient');
      await fb.sendPasswordResetEmail(fb.getFirebaseAuth(), email);
      setResetSent(true);
    } catch {
      setError('Invio email di reset non riuscito. Riprova.');
    }
  };

  if (mode === 'bind') {
    return (
      <FocusedPageShell
        title="Collega il tuo account"
        description="L'accesso è riuscito ma l'account non è ancora collegato a una richiesta di onboarding."
        footer={
          <PillButton
            type="button"
            size="lg"
            className="w-full"
            disabled={busy}
            onClick={() => void handleBind()}
          >
            Collega l'account e apri la richiesta
          </PillButton>
        }
      >
        <div className="grid gap-3">
          <Banner tone="info" title="Un ultimo passaggio">
            Probabilmente la registrazione si era interrotta prima del
            collegamento. Completa la verifica anti-abuso e riprendi da dove
            eri.
          </Banner>
          <Turnstile
            key={turnstileKey}
            siteKey={env.VITE_TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken('')}
            onExpire={() => setTurnstileToken('')}
            options={{ theme: 'light' }}
          />
          {error ? <FieldError message={error} /> : null}
        </div>
      </FocusedPageShell>
    );
  }

  return (
    <FocusedPageShell
      title="Accedi all'area onboarding"
      description="Riprendi la tua richiesta di collaborazione con email e password."
      footer={
        <PillButton
          type="button"
          size="lg"
          className="w-full"
          disabled={busy}
          onClick={() => void handleLogin()}
        >
          Accedi
        </PillButton>
      }
    >
      <div className="grid gap-4">
        <FloatingLabelInput
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <FloatingLabelInput
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <PillButton
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => void handlePasswordReset()}
          >
            Password dimenticata?
          </PillButton>
          {resetSent ? <span>Email di reset inviata.</span> : null}
        </div>
        {error ? <FieldError message={error} /> : null}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Non hai un account?</span>
          <PillButton
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => navigate('/registrazione-intermediario')}
          >
            Compila la richiesta di registrazione
          </PillButton>
        </div>
      </div>
    </FocusedPageShell>
  );
}
